import { Fragment } from 'react';
import { View, Image } from '@react-pdf/renderer';
import { mapValues, round, values } from 'lodash';

/**
* @function Canvas 
* @description Renders heatmap visualizations in PDF using canvas-generated images.
* Supports both container and area-specific views with subdivisions.
*
* @param {Object} props
* @param {boolean} props.inContainer - Whether rendering at container level
* @param {string} props.areaId - Specific area ID to render
* @param {Object} props.content - Room configuration data
* @param {Object} props.content.areas - Areas data by ID
* @param {string} props.device - Device type for plot data
* @param {number} props.scale - Scale factor for dimensions
* @param {Object} props.plotProps - Plot configuration properties
* @param {Object} props.gradient - Color gradient mapping
*
* @description
* Generates heatmap visualization by:
* 1. Processing area and subdivision data:
*    - Handles container vs area-specific views
*    - Maps coordinates to PDF space
*    - Processes subdivisions for layout plots
* 2. Creating canvas images for each subdivision:
*    - Maps plot values to colors
*    - Applies gradient based on value ranges
*    - Handles alpha channel for transparency
* 3. Positioning images in PDF:
*    - Aligns with area boundaries
*    - Adjusts for subdivisions
*    - Maintains proper scaling
*
* @function getImage
* @description Helper function that generates heatmap image for a section
* @param {Object} style - Position and dimension styles
* @param {number} scale - Scale factor
* @param {Object} plotProps - Plot configuration
* @param {Object} data - Plot data
* @param {Object} gradient - Color gradient mapping
* @returns {string} PNG data URL of generated heatmap
*
* @example
* <Canvas
*   inContainer={true}
*   device="speakers"
*   scale={0.5}
*   plotProps={{
*     minLevel: 60,
*     maxLevel: 90,
*     accuracy: 10
*   }}
*   content={{
*     areas: {
*       room1: {
*         location: {top: 10, left: 10},
*         dimensions: {length: 100, width: 80},
*         speakerLayout: [...]
*       }
*     }
*   }}
* />
*
* @note
* - Uses canvas for image generation
* - Converts canvas to PNG for PDF compatibility
* - Handles both speaker layout and device plot data
* - Supports area subdivisions with proper positioning
* - Maintains precise pixel-level color mapping
* - Adds 1px offset for grid alignment
*/
const getImage = (style, scale, plotProps, data, gradient) => {
  const { top, left, width, height } = style;
  const { minLevel, maxLevel, accuracy, pointToPixelValue } = plotProps;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  //set dimensions
  canvas.width = width;
  canvas.height = height;
  // Fill it
  const heatmapimage = context.createImageData(width, height);
  const heatmapdata = heatmapimage.data;
  const plot = Array(width * height).fill(0);
  if (data.mapArray) {
    for (var i = 0; i < plot.length; i++) {
      const y = Math.floor(i / width);
      const x = i - y * width;
      const dx = (x + left) / scale;
      const dy = (y + top) / scale;
      let pixelLevel = pointToPixelValue({ x: dx, y: dy, px: x, py: y }, data)
      pixelLevel = Math.max(minLevel, Math.min(maxLevel, pixelLevel));
      const rgb = gradient[round(pixelLevel * accuracy)];
      const baseindex = 4 * i;
      if (!rgb || rgb.length < 3) continue;
      heatmapdata[baseindex] = rgb[0]; //red
      heatmapdata[baseindex + 1] = rgb[1]; //green
      heatmapdata[baseindex + 2] = rgb[2]; //blue
      heatmapdata[baseindex + 3] = (rgb[3] ?? 1) * 255; //alpha
    };
  };
  context.putImageData(heatmapimage, 0, 0);
  return canvas.toDataURL("image/png");
};


const Canvas = (props) => {
  const { inContainer = false, areaId = null, content: { areas }, device, scale, plotProps, gradient } = props;
  const areaArray = [...(inContainer ? values(areas) : [areas[areaId]]).map(area => {
    const { id, location, dimensions, aislesArray = [{ top: 0, left: 0, length: 0, width: 0 }] } = area;
    const { top, left } = mapValues(location, (v) => round(v * scale));
    const { length, width } = mapValues(dimensions, (v) => round(v * scale));

    // Set the anchor point for the view 
    const anchor = {
      position: 'absolute',
      top: inContainer ? top : 0,
      left: inContainer ? left : 0,
      width: length,
      height: width,
    };

    const dataSource = plotProps?.source;
    let combinedArray = [];
    if (dataSource) {
      combinedArray = area.speakerLayout.map(layout => {
        const id = layout.id;
        const regex = /\s(?<type>[a-z]+)(?<aisleIndex>\d+)/g;
        const { type = null, aisleIndex = null } = regex.exec(id)?.groups || {};
        const subdivision = (type && aisleIndex) ? area.aislesArray[+aisleIndex] : { top: 0, left: 0, length: 0, width: 0 };
        return {
          subdivision,
          data: {
            id: id,
            receiverSpacing: layout.receiverSpacing,
            receiverXOffset: layout.receiverXOffset,
            receiverYOffset: layout.receiverYOffset,
            mapArray: layout[dataSource]
          }
        };
      });
    } else {
      combinedArray = [{
        subdivision: { top: 0, left: 0, length: 0, width: 0 },
        data: { ...area[device].plotData }
      }]
    };

    const plots = combinedArray.map(section => {
      const { subdivision, data } = section
      const { top, left, length: secLength, width: secWidth } = mapValues(subdivision, (v) => {
        return round(v * scale)
      });
      const subWidth = secLength ? secLength : length;
      const subHeight = secWidth ? secWidth : width;
      //Subsection position and size 
      const style = {
        position: 'absolute',
        top: 1 + top + (Boolean(subdivision.type) ? 1 : 0), // + !isNull(sec.type),
        left: 1 + left + (Boolean(subdivision.type) ? 1 : 0), // + !isNull(sec.type),
        width: subWidth,
        height: subHeight
      };
      const image = getImage(style, scale, plotProps, data, gradient)
      return { style, image }
    })

    return {
      id: `${id}-heatmap`,
      anchor,
      plots,
    }
  }
  )];

  return (
    <Fragment>
      {areaArray.map((area, index) => {
        return (
          <View key={index} style={area.anchor}  >
            {area.plots.map((sub, i) =>
              <Image key={`subplot-${i}`} src={sub.image} style={sub.style} />
            )}
          </View>
        )
      })}
    </Fragment>
  )
};

export default Canvas;

